import { supabase } from './supabase';
import { SHOWCASE_LISTINGS } from '../data/showcaseListings';
import type {
  MarketplaceBillingInterval,
  MarketplaceCategoryKey,
  MarketplaceFact,
  MarketplaceFilters,
  MarketplaceListing,
  MarketplaceListingType,
  MarketplaceQueryResult,
  MarketplaceSuggestion,
} from '../types/marketplace';
import { isProtectedListingReference } from '../services/listingContentUrlPolicy';

export const MARKETPLACE_CATEGORY_DEFINITIONS = [
  {
    key: 'human-made',
    label: 'Human Made',
    description: 'Authentic creations handcrafted and designed directly by independent creators',
    href: '/human-made',
  },
  {
    key: 'digital-products',
    label: 'Digital Products',
    description: 'Templates, ebooks, software and creative assets',
    href: '/digital-products',
  },
  {
    key: 'services',
    label: 'Services',
    description: 'Hire designers, developers, marketers and other experts',
    href: '/services',
  },
  {
    key: 'courses',
    label: 'Courses',
    description: 'Learn through structured lessons, workshops and coaching',
    href: '/courses',
  },
  {
    key: 'communities',
    label: 'Communities',
    description: 'Join groups built around shared goals and interests',
    href: '/communities',
  },
] as const;

// Compatibility projection for the short rollout window before the sanitized
// catalog RPC is live. It intentionally omits metadata and every delivery field;
// CTA/fact helpers therefore use their honest defaults during that window.
const MARKETPLACE_SAFE_FALLBACK_SELECT = [
  'id',
  'seller_id',
  'title',
  'slug',
  'description',
  'short_description',
  'type',
  'category',
  'price',
  'base_currency',
  'pricing_type',
  'billing_interval',
  'images',
  'image_url',
  'thumbnail',
  'share_image_url',
  'rating_avg',
  'rating_count',
  'status',
  'is_sandbox',
  'deleted_at',
  'dsa_seller_verification_status',
  'created_at',
  'published_at',
  'profiles!listings_seller_id_fkey(id,username,full_name,avatar_url)',
].join(',');

const PUBLIC_MARKETPLACE_RAW_TYPES = [
  'digital',
  'apps',
  'virtual',
  'bundle',
  'music',
  'service',
  'course',
  'community',
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const parseJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export const parseMarketplaceMetadata = (
  value: unknown,
): Record<string, unknown> => {
  if (isRecord(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return {};
  const parsed = parseJson(value);
  return isRecord(parsed) ? parsed : {};
};

const cleanUrl = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const safeFallbackCatalogText = (value: unknown, maximum: number) => {
  if (typeof value !== 'string') return null;
  const candidate = value.trim();
  return candidate && candidate.length <= maximum && !isProtectedListingReference(candidate)
    ? candidate
    : null;
};

const safeFallbackCatalogUrl = (value: unknown) => {
  const candidate = safeFallbackCatalogText(value, 2048);
  if (!candidate) return null;
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'https:' || parsed.username || parsed.password || parsed.hash) return null;
    if (parsed.port && parsed.port !== '443') return null;
    return parsed.toString();
  } catch {
    return null;
  }
};

const scrubFallbackCatalogRow = (value: unknown) => {
  if (!isRecord(value)) return {};
  const profileValue = Array.isArray(value.profiles) ? value.profiles[0] : value.profiles;
  const profile = isRecord(profileValue) ? profileValue : {};
  return {
    ...value,
    title: safeFallbackCatalogText(value.title, 300),
    slug: safeFallbackCatalogText(value.slug, 200),
    description: safeFallbackCatalogText(value.description, 10_000),
    short_description: safeFallbackCatalogText(value.short_description, 600),
    image_url: safeFallbackCatalogUrl(value.image_url),
    thumbnail: safeFallbackCatalogUrl(value.thumbnail),
    share_image_url: safeFallbackCatalogUrl(value.share_image_url),
    images: Array.isArray(value.images)
      ? value.images.slice(0, 24).map(safeFallbackCatalogUrl).filter(Boolean)
      : [],
    profiles: {
      ...profile,
      username: safeFallbackCatalogText(profile.username, 100),
      full_name: safeFallbackCatalogText(profile.full_name, 200),
      avatar_url: safeFallbackCatalogUrl(profile.avatar_url),
    },
  };
};

export const parseMarketplaceImages = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map(cleanUrl).filter((item): item is string => Boolean(item));
  }
  if (isRecord(value)) {
    return Object.values(value)
      .map(cleanUrl)
      .filter((item): item is string => Boolean(item));
  }
  if (typeof value !== 'string' || !value.trim()) return [];
  const parsed = parseJson(value);
  if (parsed !== null) return parseMarketplaceImages(parsed);
  return [value.trim()];
};

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const finiteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const normalized =
    typeof value === 'string'
      ? value.replace(/[^\d.,-]/g, '').replace(',', '.')
      : value;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
};

const positiveInteger = (value: unknown): number | null => {
  const number = finiteNumber(value);
  return number !== null && number > 0 ? Math.round(number) : null;
};

const firstRecord = (value: unknown): Record<string, unknown> => {
  if (isRecord(value)) return value;
  if (Array.isArray(value) && isRecord(value[0])) return value[0];
  return {};
};

const nested = (
  source: Record<string, unknown>,
  ...keys: string[]
): unknown => {
  let value: unknown = source;
  for (const key of keys) {
    if (!isRecord(value)) return undefined;
    value = value[key];
  }
  return value;
};

const firstValue = (...values: unknown[]) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const normalizeBillingInterval = (
  value: unknown,
): MarketplaceBillingInterval | null => {
  const normalized = text(value)?.toLowerCase().replace(/^per[\s_-]*/, '');
  if (!normalized) return null;
  if (['day', 'daily'].includes(normalized)) return 'day';
  if (['week', 'weekly'].includes(normalized)) return 'week';
  if (['month', 'monthly'].includes(normalized)) return 'month';
  if (['year', 'yearly', 'annual', 'annually'].includes(normalized)) return 'year';
  return null;
};

const classifyListing = (
  rawType: unknown,
  metadata: Record<string, unknown>,
): MarketplaceListingType | null => {
  const value = text(rawType)?.toLowerCase().replace(/[\s_]+/g, '-');
  const digitalType = text(
    firstValue(
      metadata.digitalType,
      metadata.digital_type,
      nested(metadata, 'course', 'type'),
    ),
  )?.toLowerCase();

  if (value === 'service') return 'service';
  if (value === 'course' || digitalType === 'course') return 'course';
  if (value === 'community') return 'community';
  if (['digital', 'apps', 'virtual', 'bundle', 'music'].includes(value || '')) {
    return 'digital-product';
  }
  return null;
};

export const isAiMadeListing = (listing: any): boolean => {
  if (!listing) return false;
  const meta = (typeof listing.metadata === 'object' && listing.metadata !== null) ? listing.metadata : {};
  const raw = (typeof listing.raw === 'object' && listing.raw !== null) ? listing.raw : {};
  const rawMeta = (typeof raw.metadata === 'object' && raw.metadata !== null) ? raw.metadata : {};

  return Boolean(
    meta.studioGenerated === true ||
    meta.studio_generated === true ||
    meta.creation_mode === 'wersee_ai' ||
    meta.creationMode === 'wersee_ai' ||
    meta.ai_generated === true ||
    meta.aiGenerated === true ||
    meta.created_with_ai === true ||
    meta.origin === 'wersee_ai' ||
    meta.ai_creator === 'wersee_ai' ||
    meta.generator === 'wersee-ai' ||
    rawMeta.studioGenerated === true ||
    rawMeta.studio_generated === true ||
    rawMeta.creation_mode === 'wersee_ai' ||
    raw.creation_mode === 'wersee_ai' ||
    raw.studio_generated === true ||
    listing.creation_mode === 'wersee_ai' ||
    listing.studioGenerated === true ||
    listing.ai_generated === true
  );
};

export const isHumanMadeListing = (listing: any): boolean => !isAiMadeListing(listing);

export const getListingOrigin = (listing: any): 'wersee_ai' | 'human_made' =>
  isAiMadeListing(listing) ? 'wersee_ai' : 'human_made';

export const categoryKeyForListing = (
  listing: Pick<MarketplaceListing, 'type'> & { category?: string | null },
): MarketplaceCategoryKey => {
  const cat = listing.category?.toLowerCase();
  if (cat === 'human made' || cat === 'human-made') {
    return 'human-made';
  }
  switch (listing.type) {
    case 'service':
      return 'services';
    case 'course':
      return 'courses';
    case 'community':
      return 'communities';
    default:
      return 'digital-products';
  }
};

const normalizeCurrency = (value: unknown): string | null => {
  const normalized = text(value)?.toUpperCase();
  return normalized && /^[A-Z]{3}$/.test(normalized) ? normalized : null;
};

const normalizeUsername = (value: unknown): string | null => {
  const normalized = text(value)?.replace(/^@/, '');
  return normalized && /^[A-Za-z0-9._-]+$/.test(normalized)
    ? normalized
    : null;
};

const normalizeSlug = (value: unknown): string | null => {
  const normalized = text(value);
  return normalized && !/[/\\?#]/.test(normalized) ? normalized : null;
};

export const getMarketplaceListingHref = (
  id: string,
  username: string | null,
  slug: string | null,
) =>
  username && slug
    ? `/@${encodeURIComponent(username)}/${encodeURIComponent(slug)}`
    : `/listing/${encodeURIComponent(id)}`;

const normalizePlans = (value: unknown): Record<string, unknown>[] => {
  if (typeof value === 'string') return normalizePlans(parseJson(value));
  return Array.isArray(value) ? value.filter(isRecord) : [];
};

const priceDetails = (
  raw: Record<string, unknown>,
  metadata: Record<string, unknown>,
) => {
  const directPrice = finiteNumber(raw.price);
  const plans = normalizePlans(firstValue(raw.plans, metadata.plans));
  const packages = normalizePlans(metadata.packages);
  const candidatePrices = [...plans, ...packages]
    .map((item) => finiteNumber(item.price))
    .filter((item): item is number => item !== null);
  const packageMinimum = candidatePrices.length
    ? Math.min(...candidatePrices)
    : null;

  if (packageMinimum !== null && (directPrice === null || packageMinimum < directPrice)) {
    return { price: packageMinimum, prefix: 'from' as const };
  }
  return { price: directPrice, prefix: null };
};

const listingFacts = (
  type: MarketplaceListingType,
  metadata: Record<string, unknown>,
  billingInterval: MarketplaceBillingInterval | null,
): MarketplaceFact[] => {
  const facts: Array<MarketplaceFact | null> = [];
  const push = (label: string, value: unknown) => {
    const display = text(value);
    if (display) facts.push({ label, value: display });
  };

  if (type === 'digital-product') {
    push('Format', firstValue(metadata.format, metadata.fileType, metadata.file_type));
    push('Licence', firstValue(metadata.license, metadata.licenseName, metadata.license_name));
    push('Access', firstValue(metadata.access, metadata.delivery, metadata.deliveryMethod));
  }

  if (type === 'service') {
    const packages = normalizePlans(metadata.packages);
    const firstPackage = packages[0] || {};
    const deliveryDays = positiveInteger(
      firstValue(firstPackage.deliveryTime, metadata.deliveryTime, metadata.delivery_days),
    );
    if (deliveryDays) facts.push({ label: 'Delivery', value: `${deliveryDays} days` });
    const revisions = positiveInteger(firstValue(firstPackage.revisions, metadata.revisions));
    if (revisions) facts.push({ label: 'Revisions', value: String(revisions) });
  }

  if (type === 'course') {
    push('Duration', firstValue(metadata.duration, nested(metadata, 'course', 'duration')));
    const lessonCount = positiveInteger(
      firstValue(metadata.lessonCount, metadata.lesson_count, nested(metadata, 'course', 'lessonCount')),
    );
    if (lessonCount) facts.push({ label: 'Lessons', value: String(lessonCount) });
    push('Access', firstValue(metadata.accessPeriod, metadata.access_period));
  }

  if (type === 'community') {
    if (billingInterval) {
      facts.push({ label: 'Billing', value: `Per ${billingInterval}` });
    }
    const memberCount = positiveInteger(
      firstValue(metadata.memberCount, metadata.member_count, metadata.members),
    );
    if (memberCount) facts.push({ label: 'Members', value: memberCount.toLocaleString() });
    push('Trial', firstValue(metadata.trial, metadata.trialPeriod, metadata.trial_period));
  }

  return facts.filter((fact): fact is MarketplaceFact => Boolean(fact)).slice(0, 2);
};

export const normalizeMarketplaceListing = (
  input: unknown,
): MarketplaceListing | null => {
  if (!isRecord(input)) return null;
  const id = text(input.id);
  const title = text(input.title);
  const metadata = parseMarketplaceMetadata(input.metadata);
  const type = classifyListing(input.type, metadata);
  if (!id || !title || !type) return null;

  const profile = firstRecord(input.profiles);
  const sellerId = text(input.seller_id) || text(profile.id) || '';
  const username = normalizeUsername(
    firstValue(profile.username, input.seller_handle, metadata.seller_username),
  );
  const slug = normalizeSlug(input.slug);
  const price = priceDetails(input, metadata);
  const billingInterval = normalizeBillingInterval(
    firstValue(
      input.billing_interval,
      metadata.billing_interval,
      metadata.billingInterval,
      metadata.subscription_interval,
    ),
  );
  const pricingTypeText = text(input.pricing_type)?.toLowerCase();
  const pricingType =
    billingInterval || pricingTypeText === 'subscription' || pricingTypeText === 'recurring'
      ? 'recurring'
      : pricingTypeText === 'one_time' || pricingTypeText === 'one-time'
        ? 'one-time'
        : null;
  const images = [
    ...parseMarketplaceImages(input.images),
    ...parseMarketplaceImages(metadata.images),
  ];
  const directImages = [
    cleanUrl(input.image_url),
    cleanUrl(input.thumbnail),
    cleanUrl(input.share_image_url),
  ].filter((item): item is string => Boolean(item));
  const allImages = [...new Set([...images, ...directImages])];
  const ratingCount = positiveInteger(input.rating_count) || 0;
  const ratingAverage =
    ratingCount > 0 ? finiteNumber(input.rating_avg) : null;
  const verificationStatus = text(input.dsa_seller_verification_status)?.toLowerCase();
  const featured =
    metadata.featured === true ||
    metadata.featured_by_wersee === true ||
    metadata.featuredByWersee === true;

  return {
    id,
    slug,
    title,
    description: text(input.description),
    shortDescription: text(input.short_description),
    type,
    category: text(input.category),
    price: price.price,
    pricePrefix: price.prefix,
    currency: normalizeCurrency(
      firstValue(input.base_currency, metadata.currency, metadata.base_currency),
    ),
    billingInterval,
    pricingType,
    images: allImages,
    imageUrl: allImages[0] || null,
    metadata,
    ratingAverage,
    ratingCount,
    createdAt: text(input.created_at),
    publishedAt: text(input.published_at),
    seller: {
      id: sellerId,
      username,
      name: text(firstValue(profile.full_name, profile.username)),
      avatarUrl: cleanUrl(profile.avatar_url),
      verification:
        verificationStatus === 'verified'
          ? {
              kind: 'marketplace-compliance',
              label: 'Seller information verified for marketplace compliance',
            }
          : null,
    },
    href: getMarketplaceListingHref(id, username, slug),
    featured,
    facts: listingFacts(type, metadata, billingInterval),
    raw: input,
  };
};

export const formatMarketplacePrice = (
  listing: Pick<
    MarketplaceListing,
    'price' | 'pricePrefix' | 'currency' | 'billingInterval' | 'pricingType'
  >,
) => {
  if (listing.price === null || !listing.currency) return 'Price unavailable';
  if (listing.price === 0) return 'Free';
  let formatted: string;
  try {
    formatted = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: listing.currency,
      maximumFractionDigits: Number.isInteger(listing.price) ? 0 : 2,
    }).format(listing.price);
  } catch {
    formatted = `${listing.currency} ${listing.price.toFixed(
      Number.isInteger(listing.price) ? 0 : 2,
    )}`;
  }
  const prefix = listing.pricePrefix === 'from' ? 'From ' : '';
  const cadence =
    listing.pricingType === 'recurring' && listing.billingInterval
      ? `/${listing.billingInterval}`
      : listing.pricingType === 'one-time'
        ? ' one-time'
        : '';
  return `${prefix}${formatted}${cadence}`;
};

const listingSearchText = (listing: MarketplaceListing) =>
  [
    listing.title,
    listing.description,
    listing.shortDescription,
    listing.category,
    listing.type,
    listing.seller.username,
    listing.seller.name,
    ...Object.values(listing.metadata).filter(
      (value): value is string => typeof value === 'string',
    ),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const matchesFilters = (
  listing: MarketplaceListing,
  filters?: Partial<MarketplaceFilters>,
) => {
  if (!filters) return true;
  const query = filters.query?.trim().toLowerCase();
  if (query && !listingSearchText(listing).includes(query)) return false;
  if (filters.type && filters.type !== 'all') {
    if (filters.type === 'human-made') {
      if (isAiMadeListing(listing)) return false;
    } else if (categoryKeyForListing(listing) !== filters.type) {
      return false;
    }
  }
  if (
    filters.category &&
    listing.category?.toLowerCase() !== filters.category.toLowerCase()
  ) {
    return false;
  }
  if (filters.price === 'free' && listing.price !== 0) return false;
  if (
    filters.price === 'paid' &&
    (listing.price === null || listing.price <= 0)
  ) {
    return false;
  }
  if (
    filters.billing === 'one-time' &&
    listing.pricingType !== 'one-time'
  ) {
    return false;
  }
  if (
    filters.billing === 'recurring' &&
    listing.pricingType !== 'recurring'
  ) {
    return false;
  }
  if (
    filters.minimumPrice !== null &&
    filters.minimumPrice !== undefined &&
    (listing.price === null || listing.price < filters.minimumPrice)
  ) {
    return false;
  }
  if (
    filters.maximumPrice !== null &&
    filters.maximumPrice !== undefined &&
    (listing.price === null || listing.price > filters.maximumPrice)
  ) {
    return false;
  }
  if (
    filters.minimumRating !== null &&
    filters.minimumRating !== undefined &&
    (listing.ratingAverage === null ||
      listing.ratingAverage < filters.minimumRating)
  ) {
    return false;
  }
  return true;
};

const sortListings = (
  listings: MarketplaceListing[],
  filters?: Partial<MarketplaceFilters>,
) => {
  const sort = filters?.sort || 'newest';
  const sorted = [...listings];
  if (sort === 'price-asc') {
    return sorted.sort(
      (a, b) =>
        (a.price ?? Number.POSITIVE_INFINITY) -
        (b.price ?? Number.POSITIVE_INFINITY),
    );
  }
  if (sort === 'price-desc') {
    return sorted.sort(
      (a, b) =>
        (b.price ?? Number.NEGATIVE_INFINITY) -
        (a.price ?? Number.NEGATIVE_INFINITY),
    );
  }
  if (sort === 'rating') {
    return sorted.sort(
      (a, b) =>
        (b.ratingAverage ?? -1) - (a.ratingAverage ?? -1) ||
        b.ratingCount - a.ratingCount,
    );
  }
  const query = filters?.query?.trim().toLowerCase();
  if (sort === 'relevance' && query) {
    return sorted.sort((a, b) => {
      const aTitle = a.title.toLowerCase();
      const bTitle = b.title.toLowerCase();
      const aScore = aTitle === query ? 3 : aTitle.startsWith(query) ? 2 : aTitle.includes(query) ? 1 : 0;
      const bScore = bTitle === query ? 3 : bTitle.startsWith(query) ? 2 : bTitle.includes(query) ? 1 : 0;
      return bScore - aScore;
    });
  }
  return sorted.sort(
    (a, b) =>
      new Date(b.publishedAt || b.createdAt || 0).getTime() -
      new Date(a.publishedAt || a.createdAt || 0).getTime(),
  );
};

const fetchMarketplaceCatalogPage = async (limit: number, offset: number) => {
  const catalog = await supabase.rpc('get_public_listing_catalog', {
    p_limit: limit,
    p_offset: offset,
  });
  let data = catalog.data as unknown[] | null;

  if (catalog.error) {
    const missingRpc = ['PGRST202', 'PGRST204', '42883'].includes(String(catalog.error.code || ''))
      || /get_public_listing_catalog|function .* does not exist/i.test(catalog.error.message || '');
    if (!missingRpc) throw catalog.error;

    const fallback = await supabase
      .from('listings')
      .select(MARKETPLACE_SAFE_FALLBACK_SELECT)
      .eq('status', 'published')
      .eq('is_sandbox', false)
      .is('deleted_at', null)
      .in('type', PUBLIC_MARKETPLACE_RAW_TYPES)
      .order('published_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .range(offset, offset + limit - 1);
    if (fallback.error) throw fallback.error;
    data = (fallback.data || []).map(scrubFallbackCatalogRow);
  }

  return data || [];
};

export const fetchMarketplaceListings = async (
  options: {
    limit?: number;
    offset?: number;
    filters?: Partial<MarketplaceFilters>;
  } = {},
): Promise<MarketplaceQueryResult> => {
  const requestedLimit = Math.max(Math.floor(options.limit || 120), 1);
  const offset = Math.max(Math.floor(options.offset || 0), 0);

  try {
    if (options.filters) {
      const rawListings: unknown[] = [];
      const rawPageSize = 200;
      let rawOffset = 0;

      while (true) {
        const page = await fetchMarketplaceCatalogPage(rawPageSize, rawOffset);
        rawListings.push(...page);
        if (page.length < rawPageSize) break;
        rawOffset += page.length;
      }

      const filtered = sortListings(
        rawListings
          .map((item) => normalizeMarketplaceListing(item))
          .filter((item): item is MarketplaceListing => Boolean(item))
          .filter((item) => matchesFilters(item, options.filters)),
        options.filters,
      );

      if (filtered.length > 0) {
        const end = offset + requestedLimit;
        return {
          listings: filtered.slice(offset, end),
          truncated: filtered.length > end,
        };
      }
    } else {
      const limit = Math.min(requestedLimit, 200);
      const data = await fetchMarketplaceCatalogPage(limit, offset);

      const normalized = data
        .map((item) => normalizeMarketplaceListing(item))
        .filter((item): item is MarketplaceListing => Boolean(item))
        .filter((item) => matchesFilters(item));

      if (normalized.length > 0) {
        return {
          listings: sortListings(normalized),
          truncated: data.length === limit,
        };
      }
    }
  } catch (error) {
    console.warn('[Marketplace] Live catalog unavailable, displaying showcase inventory:', error);
  }

  // Guaranteed resilient fallback to verified showcase inventory
  const fallbackFiltered = sortListings(
    SHOWCASE_LISTINGS.filter((item) => matchesFilters(item, options.filters)),
    options.filters,
  );
  const end = offset + requestedLimit;

  return {
    listings: fallbackFiltered.slice(offset, end),
    truncated: fallbackFiltered.length > end,
  };
};

const creatorHref = (username: string) => `/@${encodeURIComponent(username)}`;

export const fetchMarketplaceSuggestions = async (
  query: string,
): Promise<MarketplaceSuggestion[]> => {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery.length < 2) return [];
  const { listings } = await fetchMarketplaceListings({ limit: 80 });
  const matchingListings = listings
    .filter((listing) => listingSearchText(listing).includes(normalizedQuery))
    .slice(0, 5);
  const categorySuggestions = MARKETPLACE_CATEGORY_DEFINITIONS
    .filter((category) =>
      `${category.label} ${category.description}`.toLowerCase().includes(normalizedQuery),
    )
    .map<MarketplaceSuggestion>((category) => ({
      kind: 'category',
      id: category.key,
      label: category.label,
      detail: category.description,
      href: category.href,
      imageUrl: null,
    }));
  const creators = new Map<string, MarketplaceSuggestion>();
  listings.forEach((listing) => {
    const username = listing.seller.username;
    const creatorText = `${listing.seller.name || ''} ${username || ''}`.toLowerCase();
    if (!username || !creatorText.includes(normalizedQuery) || creators.has(username)) return;
    creators.set(username, {
      kind: 'creator',
      id: listing.seller.id || username,
      label: listing.seller.name || username,
      detail: `@${username}`,
      href: creatorHref(username),
      imageUrl: listing.seller.avatarUrl,
    });
  });

  return [
    ...matchingListings.map<MarketplaceSuggestion>((listing) => ({
      kind: 'listing',
      id: listing.id,
      label: listing.title,
      detail: listing.seller.name || listing.seller.username,
      href: listing.href,
      imageUrl: listing.imageUrl,
    })),
    ...categorySuggestions,
    ...[...creators.values()].slice(0, 4),
  ];
};
